/** Trade-log data source — real backend now.
 *
 * Calls `GET /api/trades` and `GET /api/alerts`. Backend returns snake_case;
 * this module converts to the camelCase view-model in `@/types/tradeLog`.
 *
 * Range mapping is "last N days ending today" — simplest MVP semantics.
 * Range labels can be tightened to calendar week/month later. */

import { getJson } from "@/lib/http";
import {
  ALL_STRATEGIES,
  type AlertLogEntry,
  type DateRangeKey,
  type ExitReason,
  type Horizon,
  type LegStatus,
  type TradeLegEntry,
  type TradeLogEntry,
  type TradeLogFilters,
} from "@/types/tradeLog";

interface TradeLegApiResponse {
  leg_index: number;
  side: "BUY" | "SELL";
  contract_symbol: string;
  quantity: number;
  expiry: string;
  status: LegStatus;
  entry_fill_price: number | null;
  exit_fill_price: number | null;
}

interface TradeApiResponse {
  id: string;
  strategy_id: string;
  strategy_name: string;
  // Optional: an older cached payload predates them and must still parse.
  contract_expiry?: string | null;
  signal_source?: string | null;
  signal_entry?: number | null;
  signal_stop?: number | null;
  signal_target?: number | null;
  strike_rung?: string | null;
  entry_at: string;
  exit_at: string | null;
  execution_mode: "LIVE" | "PAPER";
  contract_symbol: string | null;
  bias: "CALL" | "PUT" | null;
  lots: number | null;
  quantity: number | null;
  entry_premium: number | null;
  exit_premium: number | null;
  trail_count: number;
  exit_reason: ExitReason;
  pnl: number | null;
  brokerage: number | null;
  current_sl_price: number | null;
  high_water_mark: number | null;
  target_price?: number | null;
  trail_trigger_points: number | null;
  trail_amount_points: number | null;
  trail_slippage_points: number | null;
  sl_offset_points: number | null;
  queued_exit_at?: string | null;
  // Optional (`?`) on purpose: present on every current backend, but a
  // cached/older payload without them must parse as a plain intraday row.
  horizon?: Horizon;
  product?: string | null;
  basket_expiry?: string | null;
  last_mtm?: number | null;
  net_entry_cost?: number | null;
  holding_days?: number | null;
  legs?: TradeLegApiResponse[] | null;
}

interface AlertApiResponse {
  id: string;
  strategy_id: string;
  strategy_name: string;
  received_at: string;
  kind: "ENTRY" | "EXIT";
  alert_name: string;
  accepted: boolean;
  rejection_reason: string | null;
  trade_id: string | null;
}

export interface TradeLogResponse {
  trades: TradeLogEntry[];
  alerts: AlertLogEntry[];
}

export const tradeLogApi = {
  async list(filters: TradeLogFilters): Promise<TradeLogResponse> {
    const query = buildQuery(filters);
    const [trades, alerts] = await Promise.all([
      getJson<TradeApiResponse[]>(`/api/trades${query}`),
      getJson<AlertApiResponse[]>(`/api/alerts${query}`),
    ]);
    return {
      trades: trades.map(toTrade),
      alerts: alerts.map(toAlert),
    };
  },
  async updateBrokerage(tradeId: string, brokerage: number): Promise<TradeLogEntry> {
    const raw = await getJson<TradeApiResponse>(`/api/trades/${tradeId}`, {
      method: "PATCH",
      body: JSON.stringify({ brokerage }),
    });
    return toTrade(raw);
  },
  async forceExit(tradeId: string): Promise<TradeLogEntry> {
    const raw = await getJson<TradeApiResponse>(`/api/trades/${tradeId}/force-exit`, {
      method: "POST",
    });
    return toTrade(raw);
  },
};

function buildQuery(filters: TradeLogFilters): string {
  const { from, to } = rangeToDates(filters.range);
  const params = new URLSearchParams({ from, to });
  if (filters.strategyId !== ALL_STRATEGIES) {
    params.set("strategy_id", filters.strategyId);
  }
  if (filters.executionMode !== undefined) {
    params.set("execution_mode", filters.executionMode);
  }
  return `?${params.toString()}`;
}

function rangeToDates(range: DateRangeKey): { from: string; to: string } {
  const today = todayIST();
  if (range === "today") return { from: today, to: today };
  if (range === "yesterday") {
    const y = shiftDays(today, -1);
    return { from: y, to: y };
  }
  if (range === "week") return { from: shiftDays(today, -6), to: today };
  return { from: shiftDays(today, -29), to: today };
}

/** Today's date in IST, as YYYY-MM-DD. Uses en-CA which formats as ISO. */
function todayIST(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function shiftDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T00:00:00+05:30`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

/** The engine's own account of the trade: which rule fired and the levels it
 * committed to.
 *
 * Optional on the wire so a cached payload from before 2026-08-24 still parses.
 * Absent and null mean the same thing — NOT recorded — which is why both
 * collapse to null rather than to a placeholder rule.
 *
 * Split out of `toTrade` to keep that mapper under the complexity gate. */
function toSignal(
  r: TradeApiResponse,
): Pick<
  TradeLogEntry,
  "signalSource" | "signalEntry" | "signalStop" | "signalTarget" | "contractExpiry" | "strikeRung"
> {
  return {
    signalSource: r.signal_source ?? null,
    contractExpiry: r.contract_expiry ?? null,
    signalEntry: r.signal_entry ?? null,
    signalStop: r.signal_stop ?? null,
    signalTarget: r.signal_target ?? null,
    strikeRung: r.strike_rung ?? null,
  };
}

function toTrade(r: TradeApiResponse): TradeLogEntry {
  return {
    id: r.id,
    strategyId: r.strategy_id,
    strategyName: r.strategy_name,
    ...toSignal(r),
    entryAt: r.entry_at,
    exitAt: r.exit_at,
    executionMode: r.execution_mode,
    contractSymbol: r.contract_symbol,
    bias: r.bias,
    lots: r.lots,
    quantity: r.quantity,
    entryPremium: r.entry_premium,
    exitPremium: r.exit_premium,
    trailCount: r.trail_count,
    exitReason: r.exit_reason,
    pnl: r.pnl,
    brokerage: r.brokerage,
    currentSlPrice: r.current_sl_price,
    highWaterMark: r.high_water_mark,
    targetPrice: r.target_price ?? null,
    trailTriggerPoints: r.trail_trigger_points,
    trailAmountPoints: r.trail_amount_points,
    trailSlippagePoints: r.trail_slippage_points,
    slOffsetPoints: r.sl_offset_points,
    queuedExitAt: r.queued_exit_at ?? null,
    // Defensive `??` on the basket fields: they are always present on
    // current backends, but a cached/older payload without them must
    // degrade to a plain intraday row rather than crash the table.
    horizon: r.horizon ?? "INTRADAY",
    product: r.product ?? null,
    basketExpiry: r.basket_expiry ?? null,
    lastMtm: r.last_mtm ?? null,
    netEntryCost: r.net_entry_cost ?? null,
    holdingDays: r.holding_days ?? null,
    legs: r.legs ? r.legs.map(toLeg) : null,
  };
}

function toLeg(r: TradeLegApiResponse): TradeLegEntry {
  return {
    legIndex: r.leg_index,
    side: r.side,
    contractSymbol: r.contract_symbol,
    quantity: r.quantity,
    expiry: r.expiry,
    status: r.status,
    entryFillPrice: r.entry_fill_price,
    exitFillPrice: r.exit_fill_price,
  };
}

function toAlert(r: AlertApiResponse): AlertLogEntry {
  return {
    id: r.id,
    strategyId: r.strategy_id,
    strategyName: r.strategy_name,
    receivedAt: r.received_at,
    type: r.kind,
    alertName: r.alert_name,
    decision: r.accepted ? "ACCEPTED" : "REJECTED",
    reason: r.rejection_reason,
    rawPayload: null,
  };
}
